import "@/App.css";
import { useEffect } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { ClientAuthProvider } from "@/lib/clientAuth";
import { TeamAuthProvider } from "@/lib/teamAuth";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClients from "@/pages/AdminClients";
import AdminClientDetail from "@/pages/AdminClientDetail";
import AdminProjects from "@/pages/AdminProjects";
import AdminProjectEdit from "@/pages/AdminProjectEdit";
import AdminInvoices from "@/pages/AdminInvoices";
import AdminQuestions from "@/pages/AdminQuestions";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminCareers from "@/pages/AdminCareers";
import AdminTeam from "@/pages/AdminTeam";
import CareersPage from "@/pages/CareersPage";
import CaseStudy from "@/pages/CaseStudy";
import InsightArticle from "@/pages/InsightArticle";
import PaidMedia from "@/pages/PaidMedia";
import ClientLogin from "@/pages/ClientLogin";
import ClientSetup from "@/pages/ClientSetup";
import ClientSignup from "@/pages/ClientSignup";
import ClientForgotPassword from "@/pages/ClientForgotPassword";
import ClientResetPassword from "@/pages/ClientResetPassword";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientProjectDetail from "@/pages/ClientProjectDetail";
import ClientInvoices from "@/pages/ClientInvoices";
import ClientAccount from "@/pages/ClientAccount";
import ClientHelp from "@/pages/ClientHelp";
import TeamLogin from "@/pages/TeamLogin";
import TeamSetup from "@/pages/TeamSetup";
import TeamForgotPassword from "@/pages/TeamForgotPassword";
import TeamResetPassword from "@/pages/TeamResetPassword";
import TeamDashboard from "@/pages/TeamDashboard";
import TeamClientDetail from "@/pages/TeamClientDetail";
import NotFound from "@/pages/NotFound";

function HashScroller() {
    const { hash, pathname } = useLocation();
    useEffect(() => {
        if (!hash) return;
        const id = hash.replace("#", "");
        requestAnimationFrame(() => {
            const el = document.getElementById(id);
            if (el)
                el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, [hash, pathname]);
    return null;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <ClientAuthProvider>
                    <TeamAuthProvider>
                        <BrowserRouter>
                        <HashScroller />
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/careers" element={<CareersPage />} />
                            <Route path="/work/:slug" element={<CaseStudy />} />
                            <Route
                                path="/insights/:slug"
                                element={<InsightArticle />}
                            />
                            <Route
                                path="/services/paid-media"
                                element={<PaidMedia />}
                            />

                            {/* Admin */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route
                                path="/admin/clients"
                                element={<AdminClients />}
                            />
                            <Route
                                path="/admin/clients/:id"
                                element={<AdminClientDetail />}
                            />
                            <Route
                                path="/admin/projects"
                                element={<AdminProjects />}
                            />
                            <Route
                                path="/admin/projects/:id"
                                element={<AdminProjectEdit />}
                            />
                            <Route
                                path="/admin/invoices"
                                element={<AdminInvoices />}
                            />
                            <Route
                                path="/admin/questions"
                                element={<AdminQuestions />}
                            />
                            <Route
                                path="/admin/feedback"
                                element={<AdminFeedback />}
                            />
                            <Route
                                path="/admin/careers"
                                element={<AdminCareers />}
                            />
                            <Route
                                path="/admin/team"
                                element={<AdminTeam />}
                            />

                            {/* Client portal */}
                            <Route
                                path="/portal/login"
                                element={<ClientLogin />}
                            />
                            <Route
                                path="/portal/signup"
                                element={<ClientSignup />}
                            />
                            <Route
                                path="/portal/forgot-password"
                                element={<ClientForgotPassword />}
                            />
                            <Route
                                path="/portal/reset-password"
                                element={<ClientResetPassword />}
                            />
                            <Route
                                path="/portal/setup"
                                element={<ClientSetup />}
                            />
                            <Route
                                path="/portal"
                                element={<ClientDashboard />}
                            />
                            <Route
                                path="/portal/projects/:id"
                                element={<ClientProjectDetail />}
                            />
                            <Route
                                path="/portal/invoices"
                                element={<ClientInvoices />}
                            />
                            <Route
                                path="/portal/account"
                                element={<ClientAccount />}
                            />
                            <Route
                                path="/portal/help"
                                element={<ClientHelp />}
                            />

                            {/* Team / Manager portal */}
                            <Route path="/team/login" element={<TeamLogin />} />
                            <Route path="/team/setup" element={<TeamSetup />} />
                            <Route
                                path="/team/forgot-password"
                                element={<TeamForgotPassword />}
                            />
                            <Route
                                path="/team/reset-password"
                                element={<TeamResetPassword />}
                            />
                            <Route path="/team" element={<TeamDashboard />} />
                            <Route
                                path="/team/clients/:id"
                                element={<TeamClientDetail />}
                            />

                            {/* 404 fallback */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </BrowserRouter>
                    </TeamAuthProvider>
                </ClientAuthProvider>
            </AuthProvider>
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#0f0f0f",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#F7F7F2",
                        borderRadius: "2px",
                    },
                }}
            />
        </div>
    );
}

export default App;
